import React, {FC, useCallback, useEffect, useRef, useState} from "react";
import {Alert, Box, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from "@mui/material";
import {useAppDispatch} from "../../store";
import {useLanguage} from "../../features/localisation/useLanguage";
import {convertCentsToCurrencyString} from "../../features/common_funcs";
import {useGameHistory} from "../../features/game_history/useGameHistory";
import {clearGameHistory} from "../../features/game_history/gameHistorySlice";
import GameNameMarkdownRenderer from "./game-name-renderer/GameNameMarkdownRenderer";

export const GameHistoryTable: FC = () => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState<number>(0)
    const dispatch = useAppDispatch()

    const {languagePack: {pack: {gamesHistory: lang}}} = useLanguage()
    const {gameHistory, isGameHistoryLoading} = useGameHistory()

    const updateHeight = useCallback(() => {
        if (wrapperRef.current) {
            const newHeight = wrapperRef.current.offsetHeight
            if (height !== newHeight) {
                setHeight(newHeight)
            }
        }
    }, [height])

    useEffect(() => {
        updateHeight()
        window.addEventListener('resize', updateHeight);
        return () => {
            window.removeEventListener('resize', updateHeight);
        };
    }, [updateHeight]);

    useEffect(() => {
        return () => {
            dispatch(clearGameHistory())
        }
    }, [dispatch])

    return <Box
        height={'100%'}
        ref={wrapperRef}
        flexGrow={1}>
        <TableContainer sx={{
            maxHeight: `${height}px`,
            '& .MuiTableCell-body': {
                verticalAlign: 'top',
            }
        }}>
            <Table stickyHeader aria-label="manage users table">
                <TableHead>
                    <TableRow>
                        <TableCell>
                            {lang.userNameTableHeader}
                        </TableCell>
                        <TableCell>
                            {lang.providerTableHeader}
                        </TableCell>
                        <TableCell>
                            {lang.gameTableHeader}
                        </TableCell>
                        <TableCell>
                            {lang.betAmountTableHeader}
                        </TableCell>
                        <TableCell>
                            {lang.winAmountTableHeader}
                        </TableCell>
                        <TableCell>
                            {lang.dateTimeTableHeader}
                        </TableCell>
                        <TableCell align="center">{lang.balanceBeforeAndAfterHeader}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {gameHistory && gameHistory.map((game, index) => (
                        <TableRow
                            key={game.game_name + '-gt-' + index}
                            sx={{'&:last-child td, &:last-child th': {border: 0}}}
                        >
                            <TableCell component="th" scope="row">
                                {game.user_name}
                            </TableCell>
                            <TableCell>{game.provider_name}</TableCell>
                            <TableCell>
                                {game.game_type === 'sport' ? (
                                    <GameNameMarkdownRenderer>
                                        {game.game_name}
                                    </GameNameMarkdownRenderer>
                                ) : game.game_name}
                            </TableCell>
                            <TableCell>{game.withdraw_sum ? convertCentsToCurrencyString(game.withdraw_sum) : '--'}</TableCell>
                            <TableCell>{game.deposit_sum ? convertCentsToCurrencyString(game.deposit_sum) : '--'}</TableCell>
                            <TableCell>{game.time}</TableCell>
                            <TableCell>
                                <Box
                                    display={'flex'}
                                    width={'100%'}
                                    gap={1}
                                >
                                    <Box
                                        flex={1}
                                        display={'flex'}
                                        justifyContent={'end'}
                                    >
                                        {game.balance_before !== null && game.balance_before !== undefined ? convertCentsToCurrencyString(game.balance_before) : '--'}
                                    </Box>
                                    <Box
                                        sx={theme => ({
                                            p: .4,
                                            '&::before': {
                                                content: '""',
                                                display: 'block',
                                                width: '1px',
                                                height: '100%',
                                                backgroundColor: theme.palette.text.primary,
                                            }
                                        })}
                                    />
                                    <Box
                                        flex={1}
                                    >
                                        {game.balance_after !== null && game.balance_after !== undefined ? convertCentsToCurrencyString(game.balance_after) : '--'}
                                    </Box>
                                </Box>
                            </TableCell>
                        </TableRow>
                    ))}
                    {gameHistory === null && isGameHistoryLoading && <TableRow>
                        <TableCell><Skeleton animation="wave"/></TableCell>
                        <TableCell><Skeleton animation="wave"/></TableCell>
                        <TableCell><Skeleton animation="wave"/></TableCell>
                        <TableCell><Skeleton animation="wave"/></TableCell>
                        <TableCell><Skeleton animation="wave"/></TableCell>
                        <TableCell><Skeleton animation="wave"/></TableCell>
                        <TableCell><Skeleton animation="wave"/></TableCell>
                    </TableRow>}
                </TableBody>
            </Table>
        </TableContainer>
        {gameHistory?.length === 0 && <Alert variant="outlined" severity="warning">
            {lang.gamesHistoryNotFoundMessage}
        </Alert>}
    </Box>
}